import React from "react";
import { Cpu, Sliders } from "lucide-react";
import Dropdown from "../Dropdown/Dropdown.js";

export default function LlmConfigTab({
  selectedAgent,
  handleInputChange,
  llmModels = [],
  formErrors = {},
}) {
  const modelOptions = llmModels.map(m => 
    typeof m === "string" 
      ? { label: m, value: m } 
      : { label: m.label || m.name || m.value, value: m.value || m.id }
  );

  return (
    <div className="tab-panel-content">
      <div className="config-section-simple">
        {/* Model Selection Header */}
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <Cpu size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>LLM Model Architecture</span>
        </div>
        
        {/* AI Model Dropdown */}
        <div style={{ marginBottom: 24 }}>
          <label className="field-label" style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: 600, color: "var(--slate-700)" }}>
            Select AI Model Engine <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>
          </label>
          <Dropdown
            options={modelOptions}
            value={selectedAgent?.ai_model || selectedAgent?.llm_type || ""}
            onChange={(e) => handleInputChange("ai_model", e.target.value)}
            placeholder="Select AI Model"
          />
          {formErrors.ai_model ? (
            <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "4px" }}>
              {formErrors.ai_model}
            </div>
          ) : (
            <div className="field-helper" style={{ marginTop: "6px" }}>
              Chooses the core language reasoning model that powers conversational turn generation.
            </div>
          )}
        </div>

        {/* Model Parameters Header */}
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px", marginTop: "24px" }}>
          <Sliders size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>LLM Parameters & Tuning</span>
        </div>

        {/* Grid Container for LLM Settings */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
          {/* 1. Temperature Slider */}
          <div className="slider-group" style={{ marginBottom: 0 }}>
            <div className="slider-header">
              <span className="field-label">
                Temperature <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
              </span>
              <span className="slider-val">{(selectedAgent?.temperature ?? 0.5).toFixed(1)}</span>
            </div>
            <input
              type="range"
              min={0.0}
              max={1.2}
              step={0.1}
              className="custom-range-slider"
              value={selectedAgent?.temperature ?? 0.5}
              onChange={(e) => handleInputChange("temperature", Number(e.target.value))}
            />
            <div className="field-helper">
              Higher values unleash creative randomness, while lower values force highly deterministic responses.
            </div>
          </div>

          {/* 2. Top-P Slider */}
          <div className="slider-group" style={{ marginBottom: 0 }}>
            <div className="slider-header">
              <span className="field-label">
                Top-P <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
              </span>
              <span className="slider-val">{(selectedAgent?.top_p ?? 0.5).toFixed(2)}</span>
            </div>
            <input
              type="range"
              min={0.0}
              max={1.0}
              step={0.05}
              className="custom-range-slider"
              value={selectedAgent?.top_p ?? 0.5}
              onChange={(e) => handleInputChange("top_p", Number(e.target.value))}
            />
            <div className="field-helper">
              Controls cumulative probability threshold for token sampling.
            </div>
          </div>

          {/* 3. Max Tokens Slider */}
          <div className="slider-group" style={{ marginBottom: 0 }}>
            <div className="slider-header">
              <span className="field-label">
                Max Response Tokens <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
              </span>
              <span className="slider-val">{selectedAgent?.max_tokens ?? 500} tokens</span>
            </div>
            <input
              type="range"
              min={50}
              max={2000}
              step={25}
              className="custom-range-slider"
              value={selectedAgent?.max_tokens ?? 500}
              onChange={(e) => handleInputChange("max_tokens", Number(e.target.value))}
            />
            <div className="field-helper">
              Limits maximum length of agent output per conversation turn to ensure concise voice responses.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
